import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import * as Yup from "yup";
import {
  useStore,
  makeid_alpa_numeric,
  remove_item_from_array,
} from "../../../helpers";
import { useParams } from "react-router-dom";
import { TextError, FormButtons } from "../../../components";
import FilePondUpload from "../../beneficiary-management-v2/beneficiary-requests/filepond";

const AddEditWhatsapp = observer((props: any) => {
  const { projectid } = useParams();
  const rootStore = useStore();
  const { voterReachOutStore } = rootStore;
  const { formType, whatsappMessageFormValue, messageType } =
    voterReachOutStore;
  const [buffer, setBuffer] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const { formSubmit, type } = props;

  const whatsappMessageFormInitialValues = {
    name: whatsappMessageFormValue.name || "",
    message: whatsappMessageFormValue.message || "",
  };

  const smsModel = {
    projectid: projectid,
    channel: type,
    more: {},
  };

  const whatsappMessageFormSubmit = async (values) => {
    try {
      const Attachments = buffer.map((file) => ({
        path: file.path,
        id: file.id,
        type: file.type,
      }));

      const smsPayload = {
        ...smsModel,
        ds: {
          name: values.name,
          text: values.message,
          attachements: Attachments,
        },
      };
      await formSubmit(smsPayload, whatsappMessageFormValue.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileUpdate = (updatedFiles) => {
    setAttachments(updatedFiles);
    const mappedBuffer = updatedFiles.map((file) => ({
      path: file.path,
      type: file.type,
      id: makeid_alpa_numeric(8),
    }));
    setBuffer(mappedBuffer);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    message: Yup.string().required("Message is required"),
  });

  const delete_file = (name) => {
    const updated_values = remove_item_from_array(buffer, "path", name);
    setBuffer(updated_values);
  };

  useEffect(() => {
    if (messageType === "whatsapp Messages" && formType === "update") {
      setBuffer(whatsappMessageFormValue.attachements);
    }
  }, [messageType, formType]);

  return (
    <div className="mx-auto max-w-2xl">
      <Formik
        initialValues={whatsappMessageFormInitialValues}
        onSubmit={whatsappMessageFormSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values }) => (
          <Form
            className="space-y-6 rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.45)] sm:p-7"
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <div className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Message name <span className="text-red-500">*</span>
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  placeholder="e.g. Volunteer follow-up template"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 shadow-sm transition-all placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-100"
                />
                <ErrorMessage name="name" component={TextError} />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Message content <span className="text-red-500">*</span>
                </label>
                <Field
                  as="textarea"
                  id="message"
                  name="message"
                  placeholder="Write your WhatsApp message here..."
                  className="h-40 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 shadow-sm transition-all placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-100"
                />
                <ErrorMessage name="message" component={TextError} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Media attachments
                  </p>
                  <p className="text-xs text-slate-500">
                    Optional images or documents to include in the message.
                  </p>
                </div>
                {buffer.length > 0 && (
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    {buffer.length} added
                  </span>
                )}
              </div>

              <div className="overflow-hidden rounded-xl border border-dashed border-slate-300 bg-white/80">
                <FilePondUpload
                  path={`project-${projectid}/voterreachout/whatsapp`}
                  files={attachments}
                  onUpdate={handleFileUpdate}
                />
              </div>

              {buffer.length > 0 && (
                <div className="mt-4 space-y-2">
                  {buffer.map((file) => (
                    <div
                      key={file.path || file.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <i className="fa-solid fa-paperclip text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-700">
                            {file.name ||
                              file.path?.split("/").pop() ||
                              "Attachment"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {file.type || "File"}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => delete_file(file.path)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                        aria-label="Remove attachment"
                      >
                        <i className="fa-solid fa-xmark text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2">
              <FormButtons
                disabled={!values.name.trim() || !values.message.trim()}
                onSave={() => {}}
                onCancel={() => {
                  voterReachOutStore.set_isSideOverlay(false);
                }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
});

export default AddEditWhatsapp;
